import React, { useEffect, useId, useState } from "react";
import { Tooltip } from "react-tooltip";
import {
  Copy,
  FileText,
  GitMerge,
  Info,
  Loader2,
  Pencil,
  Printer,
} from "lucide-react";
import moment from "moment";

import DocIcon from "assets/images/doc.svg";
import { PDFDisplayModal } from "components/Modal/PDFDisplayModal/PDFDisplayModal";
import { SendEmailModal } from "components/Modal/SendEmailModal";
import { useUserRole } from "components/Sidebar/hooks/useUserRole";
import { Button } from "components/ui/Button";
import { Dialog } from "components/ui/Dialog";
import { DialogFooter } from "components/ui/Dialog/DialogFooter";
import { Pill, PILL_COLORS } from "components/ui/Pill";
import { TruncatedText } from "components/ui/TruncatedText/TruncatedText";
import { usePatientCDMStatus } from "features/cdm/hooks/usePatientCDMStatus";
import { AddReminderModal } from "features/comunications/components/AddReminderModal/AddReminderModal";
import { AddTaskForm } from "features/comunications/components/AddTaskForm/AddTaskForm";
import { SmsModal2 } from "features/comunications/components/SmsModal2/SmsModal2";
import { MergePatientModal } from "features/patients/components/MergePatientModal/MergePatientModal";
import { PatientOverviewDrawer } from "features/patients/components/PatientOverviewDrawer/PatientOverviewDrawer";
import { PatientProfileForm } from "features/patients/components/PatientProfileForm/PatientProfileForm";
import { useCreatePatient } from "features/patients/hooks/useCreatePatient";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { useDownloadPatientReport } from "features/patients/hooks/useDownloadPatientReport";
import { usePatientData } from "features/patients/hooks/usePatientData";
import { useUpdatePatient } from "features/patients/hooks/useUpdatePatient";
import {
  formatGenderText,
  getGenderPillColors,
} from "features/patients/tables/PatientsTable/utils";
import {
  formatAddress,
  formatPatientAge,
  getInitials,
  getPatientClassificationLabel,
} from "features/patients/utils";
import { formatDateOfBirth } from "utils/dateFormatter";
import { useOpenPatientSummaryPage } from "utils/openPatientSummaryPage";

import { AllergiesAndIntolerancesAlert } from "../AllergiesAndIntolerancesAlert/AllergiesAndIntolerancesAlert";
import { PregnancyAlert } from "../PregnancyAlert/PregnancyAlert";
import styles from "./PatientDetailCard.module.css";

const PatientDetailCard = ({
  patientId: patientIdProp,
  reasonForVisit,
  showReasonForVisit,
  clickableName,
  maxWidth = 600,
} = {}) => {
  const tenantVal = localStorage.getItem("kinesin-tenant");
  const currentPatientId = useCurrentPatientId();
  const patientId = patientIdProp ?? currentPatientId;

  const editPatientFormId = useId();
  const duplicatePatientFormId = useId();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDuplicateOpen, setIsDuplicateOpen] = useState(false);
  const [isPdfOpen, setIsPdfOpen] = useState(false);
  const [isSmsOpen, setIsSmsOpen] = useState(false);
  const [isEmailOpen, setIsEmailOpen] = useState(false);
  const [isOverviewOpen, setIsOverviewOpen] = useState(false);
  const [isAddTaskOpen, setIsAddTaskOpen] = useState(false);
  const [isMergeOpen, setIsMergeOpen] = useState(false);
  const [patientCopy, setPatientCopy] = useState({});

  const { isAdmin } = useUserRole();

  const { data: patient, isPending: isLoading } = usePatientData(patientId);

  const openPatientSummaryPage = useOpenPatientSummaryPage();
  const { data: cdmStatus } = usePatientCDMStatus(patientId);

  const updatePatient = useUpdatePatient(patientId);
  const createPatient = useCreatePatient();
  const downloadPatientReport = useDownloadPatientReport();

  useEffect(() => {
    setPatientCopy(patient);
  }, [patient]);

  const handleFormSubmit = (formData) => {
    const patientData = {
      ...patientCopy,
      ...formData,
      address: {
        id: patientCopy?.address?.id,
        tenant: patientCopy?.address?.tenant || tenantVal,
        addressLine1: formData.addressLine1 || "",
        addressLine2: formData.addressLine2 || "",
        addressLine3: formData.addressLine3 || "",
        city: formData.city || "",
        county: formData.county || "",
        postCode: formData.postCode || "",
        country: formData.country ?? "",
      },
      addressId: patientCopy?.address?.id,
    };
    updatePatient.mutate(patientData, {
      onSuccess: () => setIsEditOpen(false),
    });
  };

  const handleDuplicateSubmit = (formData) => {
    const patientData = {
      ...formData,
      address: {
        addressLine1: formData.addressLine1 || "",
        addressLine2: formData.addressLine2 || "",
        addressLine3: formData.addressLine3 || "",
        city: formData.city || "",
        county: formData.county || "",
        postCode: formData.postCode || "",
        country: formData.country ?? "",
      },
    };
    createPatient.mutate(patientData, {
      onSuccess: () => setIsDuplicateOpen(false),
    });
  };

  if (isLoading) {
    return (
      <div
        className={`${styles.card} ${styles.loadingWrap}`}
        style={maxWidth ? { maxWidth: `${maxWidth}px` } : undefined}
      >
        <Loader2 size={24} className={styles.loadingSpinner} />
        <p>Loading patient data...</p>
      </div>
    );
  }

  if (!patient) return null;

  const age = formatPatientAge(patient.dateOfBirth);
  const fullName = `${patient.firstName || ""} ${patient.surname || ""}`.trim();
  const genderPillColors = getGenderPillColors(patient.gender);
  const hasEmail = Boolean(patient.email?.trim());
  const mobileTrim = patient.mobilePhone?.trim() || "";
  const homeTrim = patient.homePhone?.trim() || "";
  const phoneNumbersDisplay =
    [...new Set([mobileTrim, homeTrim].filter(Boolean))].join(", ") || "-";

  return (
    <div
      className={`${styles.card} bg`}
      style={maxWidth ? { maxWidth: `${maxWidth}px` } : undefined}
    >
      <div className={styles.header}>
        {patient.avatarUrl ? (
          <img
            src={patient.avatarUrl}
            alt="Patient"
            className={styles.avatar}
          />
        ) : (
          <div className={styles.avatar}>{getInitials(fullName)}</div>
        )}

        <div className={styles.headerInfo}>
          <div className={styles.headerTop}>
            <div className={styles.headerNameWrap}>
              <p
                className={`${styles.patientName} ${
                  clickableName ? styles.patientNameClickable : ""
                }`}
                onClick={
                  clickableName
                    ? () => openPatientSummaryPage(patient.id, fullName)
                    : undefined
                }
              >
                <span className={styles.patientNameTextWrap}>
                  {fullName} ({age})
                </span>
                <Pill
                  color={genderPillColors.color}
                  bg={genderPillColors.bg}
                  border={genderPillColors.border}
                >
                  {formatGenderText(patient.gender)}
                </Pill>
                {patient.died && (
                  <Pill
                    color={PILL_COLORS.reactionYes.color}
                    bg={PILL_COLORS.reactionYes.bg}
                    border={PILL_COLORS.reactionYes.border}
                  >
                    Deceased
                  </Pill>
                )}
              </p>
              <p className={styles.dob}>
                <span>
                  Born: {formatDateOfBirth(patient.dateOfBirth)}
                </span>
                <Info
                  size={16}
                  className={styles.infoIcon}
                  data-tooltip-id="patient-ids-tooltip"
                  data-tooltip-html={`Kinesin Patient ID: ${
                    patient.id || "-"
                  }<br/>External ID: ${patient.externalPatientId || "-"}`}
                />
              </p>
            </div>
            <div className={styles.headerActions}>
              <Button
                variant="text"
                iconButton
                onClick={() => setIsEditOpen(true)}
                aria-label="Edit"
              >
                <Pencil size={16} />
              </Button>
              {process.env.REACT_APP_IS_PATIENT_DUPLICATE_ENABLED === "true" && (
                <Button
                  variant="text"
                  iconButton
                  onClick={() => setIsDuplicateOpen(true)}
                  aria-label="Duplicate"
                >
                  <Copy size={16} />
                </Button>
              )}
              <Button
                variant="text"
                iconButton
                onClick={() => setIsPdfOpen(true)}
                aria-label="Print"
              >
                <Printer size={16} />
              </Button>
              {isAdmin && (
                <Button
                  variant="text"
                  iconButton
                  onClick={() => setIsMergeOpen(true)}
                  aria-label="Merge patient"
                >
                  <GitMerge size={16} />
                </Button>
              )}
              {isAdmin && (
                <Button
                  variant="text"
                  iconButton
                  onClick={() =>
                    downloadPatientReport.mutate({ tenantVal, patientId })
                  }
                  disabled={downloadPatientReport.isPending}
                  aria-label="Print Report"
                >
                  {downloadPatientReport.isPending ? (
                    <Loader2 size={16} className={styles.loadingSpinner} />
                  ) : (
                    <FileText size={16} />
                  )}
                </Button>
              )}
            </div>
          </div>
          <Button
            variant="outline"
            size="large"
            onClick={() => setIsOverviewOpen(true)}
          >
            Patient overview
            <img src={DocIcon} alt="" className={styles.docIcon} />
          </Button>
        </div>
      </div>

      <AllergiesAndIntolerancesAlert patientId={patient.id} />

      <PregnancyAlert patientId={patient.id} />

      {/* ── Info grid ── */}
      <div className={styles.infoGrid}>
        <div className={styles.infoColumn}>
          <div className={styles.infoBlock}>
            <span className={styles.infoLabel}>Medical card number:</span>
            <span className={styles.infoValueWithClassification}>
              <TruncatedText
                value={patient.medicalCardNumber || "-"}
                className={styles.infoValue}
                tooltipId="patient-card-tooltip-medical-card"
              />
              <span className={styles.classificationLabel}>
                ({getPatientClassificationLabel(patient.financialClass)})
              </span>
            </span>
          </div>
          <div className={styles.infoBlock}>
            <span className={styles.infoLabel}>Phone no:</span>
            <TruncatedText
              value={phoneNumbersDisplay}
              className={styles.infoValue}
              tooltipId="patient-card-tooltip-phones"
            />
          </div>
          <div className={styles.infoBlock}>
            <span className={styles.infoLabel}>Email:</span>
            <TruncatedText
              value={patient.email || "-"}
              className={styles.infoValue}
              tooltipId="patient-card-tooltip-email"
            />
          </div>
          <div className={styles.infoBlock}>
            <span className={styles.infoLabel}>Address:</span>
            <span className={styles.infoValueAddress}>
              {formatAddress(patient.address) || "-"}
            </span>
          </div>
        </div>

        <div className={styles.verticalDivider} />

        <div className={styles.infoColumn}>
          <div className={styles.infoBlock}>
            <span className={styles.infoLabel}>Registered doctor:</span>
            <TruncatedText
              value={
                patient.doctorDetails
                  ? `${patient.doctorDetails.firstName} ${patient.doctorDetails.lastName}`
                  : "Not set"
              }
              className={styles.infoValue}
              tooltipId="patient-card-tooltip-doctor"
            />
          </div>
          <div className={styles.infoBlock}>
            <span className={styles.infoLabel}>Opportunities:</span>
            <TruncatedText
              value={cdmStatus?.humanReadableSummary || "-"}
              className={styles.infoValue}
              tooltipId="patient-card-tooltip-opportunities"
            />
          </div>
          <div className={styles.infoBlock}>
            <span className={styles.infoLabel}>Upcoming appointments:</span>
            <TruncatedText
              value={
                patient.nextApppointment
                  ? moment(patient.nextApppointment).format("DD MMMM YYYY, HH:mm")
                  : "N/A"
              }
              className={styles.infoValue}
              tooltipId="patient-card-tooltip-appointments"
            />
          </div>
          <div className={styles.infoBlock}>
            <span className={styles.infoLabel}>Next of kin:</span>
            <TruncatedText
              value={
                [patient.nextOfKin, patient.nextOfKinRelationship]
                  .filter(Boolean)
                  .join(" · ") || "-"
              }
              className={styles.infoValue}
              tooltipId="patient-card-tooltip-nok-name"
            />
          </div>
          <div className={styles.infoBlock}>
            <span className={styles.infoLabel}>Next of kin phone:</span>
            <TruncatedText
              value={patient.nextOfKinPhone || "-"}
              className={styles.infoValue}
              tooltipId="patient-card-tooltip-nok-phone"
            />
          </div>
          {showReasonForVisit && (
            <div className={styles.infoBlock}>
              <span className={styles.infoLabel}>Reason for visit:</span>
              <TruncatedText
                value={reasonForVisit || "-"}
                className={styles.infoValue}
                tooltipId="patient-card-tooltip-reason"
              />
            </div>
          )}
        </div>
      </div>

      {/* ── Bottom buttons ── */}
      <div className={styles.bottomButtons}>
        <Button variant="outline" onClick={() => setIsSmsOpen(true)}>
          Send SMS
        </Button>
        <Button variant="outline" onClick={() => setIsAddTaskOpen(true)}>
          Add task
        </Button>

        <Button
          variant="outline"
          onClick={() => setIsEmailOpen(true)}
          disabled={!hasEmail}
          title={
            hasEmail
              ? undefined
              : "Add an email address on the patient record first"
          }
        >
          Send Email
        </Button>
        <AddReminderModal patient={patientCopy} buttonText="Add Reminder" />
      </div>

      {/* ── Modals ── */}
      <PatientOverviewDrawer
        open={isOverviewOpen}
        onClose={() => setIsOverviewOpen(false)}
      />

      <Dialog
        open={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Edit patient"
        width={1200}
      >
        <PatientProfileForm
          formId={editPatientFormId}
          patient={patientCopy}
          onSubmit={handleFormSubmit}
          hideSubmitButton
        />
        <DialogFooter>
          <Button
            variant="outline"
            size="large"
            onClick={() => setIsEditOpen(false)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form={editPatientFormId}
            variant="primary"
            size="large"
            disabled={updatePatient.isPending}
          >
            {updatePatient.isPending ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </Dialog>

      <Dialog
        open={isDuplicateOpen}
        onClose={() => setIsDuplicateOpen(false)}
        title="Duplicate patient"
        width={1200}
      >
        <PatientProfileForm
          formId={duplicatePatientFormId}
          patient={patientCopy}
          onSubmit={handleDuplicateSubmit}
          hideSubmitButton
        />
        <DialogFooter>
          <Button
            variant="outline"
            size="large"
            onClick={() => setIsDuplicateOpen(false)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form={duplicatePatientFormId}
            variant="primary"
            size="large"
            disabled={createPatient.isPending}
          >
            {createPatient.isPending ? "Saving..." : "Create patient"}
          </Button>
        </DialogFooter>
      </Dialog>

      <Dialog
        open={isSmsOpen}
        onClose={() => setIsSmsOpen(false)}
        title="Send SMS"
        width={1000}
      >
        <SmsModal2
          tenantVal={tenantVal}
          patient={patient}
          onClose={() => setIsSmsOpen(false)}
        />
      </Dialog>

      <SendEmailModal
        isOpen={isEmailOpen}
        onClose={() => setIsEmailOpen(false)}
        patient={patient}
      />

      <PDFDisplayModal open={isPdfOpen} onClose={() => setIsPdfOpen(false)} />

      <Dialog
        open={isAddTaskOpen}
        onClose={() => setIsAddTaskOpen(false)}
        title="Add Task"
        width={800}
      >
        <AddTaskForm
          patient={patientCopy}
          closeModal={() => setIsAddTaskOpen(false)}
        />
      </Dialog>

      {isAdmin && (
        <MergePatientModal
          open={isMergeOpen}
          onClose={() => setIsMergeOpen(false)}
          patient={patient}
        />
      )}

      <Tooltip id="patient-card-tooltip-medical-card" />
      <Tooltip id="patient-card-tooltip-phones" />
      <Tooltip id="patient-card-tooltip-email" />
      <Tooltip id="patient-card-tooltip-nok-name" />
      <Tooltip id="patient-card-tooltip-nok-phone" />
      <Tooltip id="patient-card-tooltip-doctor" />
      <Tooltip id="patient-card-tooltip-opportunities" />
      <Tooltip id="patient-card-tooltip-appointments" />
      <Tooltip id="patient-card-tooltip-reason" />
      <Tooltip id="patient-ids-tooltip" clickable />
    </div>
  );
};

export { PatientDetailCard };
